import { useState } from "preact/hooks";
import type { Company, Contact, LeadSource, Priority } from "../../types";
import { SALES_OWNERS } from "../../types";
import { Modal } from "../ui/Modal";
import { store } from "../../store/store";
import { toast } from "../shell/Toast";

export function NewOpportunityModal({
  companies,
  contacts,
  onClose,
  onCreated,
}: {
  companies: Company[];
  contacts: Contact[];
  onClose: () => void;
  onCreated: (opportunityId: string) => void;
}) {
  const [name, setName] = useState("");
  const [companyId, setCompanyId] = useState(companies[0]?.id ?? "");
  const [contactId, setContactId] = useState("");
  const [value, setValue] = useState("");
  const [probability, setProbability] = useState("30");
  const [owner, setOwner] = useState<string>(SALES_OWNERS[0]);
  const [closeDate, setCloseDate] = useState("");
  const [priority, setPriority] = useState<Priority>("Medium");
  const [source, setSource] = useState<LeadSource>("Website");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);

  const companyContacts = contacts.filter((c) => c.companyId === companyId);

  function submit(e: Event) {
    e.preventDefault();
    if (!name.trim()) {
      setError("Opportunity name is required.");
      return;
    }
    if (!companyId) {
      setError("Company is required.");
      return;
    }
    const opp = store.addOpportunity({
      name: name.trim(),
      companyId,
      primaryContactId: contactId || undefined,
      stage: "New Lead",
      value: value ? Number(value) : undefined,
      probability: Number(probability) || 0,
      salesOwner: owner,
      expectedCloseDate: closeDate ? new Date(closeDate).toISOString() : undefined,
      priority,
      source,
      description: description.trim() || "No description provided.",
    });
    toast.success("Opportunity created.");
    onCreated(opp.id);
  }

  return (
    <Modal title="New Opportunity" onClose={onClose} width={520}>
      <form class="form" onSubmit={submit}>
        {error && <p class="form-error">{error}</p>}
        <label>
          Opportunity name
          <input data-ai-id="opportunity-name" value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)} />
        </label>
        <label>
          Company
          <select
            data-ai-id="company"
            value={companyId}
            onChange={(e) => {
              setCompanyId((e.target as HTMLSelectElement).value);
              setContactId("");
            }}
          >
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Primary contact
          <select data-ai-id="primary-contact" value={contactId} onChange={(e) => setContactId((e.target as HTMLSelectElement).value)}>
            <option value="">Not selected</option>
            {companyContacts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} — {c.role}
              </option>
            ))}
          </select>
        </label>
        <div class="form-row">
          <label>
            Estimated value (€)
            <input
              type="number"
              min="0"
              data-ai-id="estimated-value"
              value={value}
              onInput={(e) => setValue((e.target as HTMLInputElement).value)}
            />
          </label>
          <label>
            Probability (%)
            <input
              type="number"
              min="0"
              max="100"
              data-ai-id="probability"
              value={probability}
              onInput={(e) => setProbability((e.target as HTMLInputElement).value)}
            />
          </label>
        </div>
        <div class="form-row">
          <label>
            Sales owner
            <select data-ai-id="sales-owner" value={owner} onChange={(e) => setOwner((e.target as HTMLSelectElement).value)}>
              {SALES_OWNERS.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </label>
          <label>
            Expected close date
            <input
              type="date"
              data-ai-id="expected-close-date"
              value={closeDate}
              onInput={(e) => setCloseDate((e.target as HTMLInputElement).value)}
            />
          </label>
        </div>
        <div class="form-row">
          <label>
            Priority
            <select data-ai-id="priority" value={priority} onChange={(e) => setPriority((e.target as HTMLSelectElement).value as Priority)}>
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
            </select>
          </label>
          <label>
            Source
            <select data-ai-id="lead-source" value={source} onChange={(e) => setSource((e.target as HTMLSelectElement).value as LeadSource)}>
              <option value="Referral">Referral</option>
              <option value="Website">Website</option>
              <option value="Outbound">Outbound</option>
              <option value="Event">Event</option>
              <option value="Partner">Partner</option>
            </select>
          </label>
        </div>
        <label>
          Description
          <textarea data-ai-id="description" value={description} onInput={(e) => setDescription((e.target as HTMLTextAreaElement).value)} />
        </label>
        <div class="form-actions">
          <button type="button" class="btn btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" class="btn btn--primary" data-ai-id="submit-create-opportunity">
            Create Opportunity
          </button>
        </div>
      </form>
    </Modal>
  );
}
